<template>
	<nuxt-link :to="localePath(`/${link}`)" class="card columns column3">
		<div :class="['card-content-header', { fullwidth: props.variant === 'promo' }]">
			<h4>{{ item.header }}{{ props.item.filename }}</h4>

			<div v-if="props.variant === 'promo'" v-parse-links v-html="item.content" />
		</div>
		<div class="card-content-button">
			<span>
				{{ item.button || $t('viewAllRooms') }}
				<font-awesome-icon icon="fa-light fa-arrow-right-long" size="1x" />
			</span>
		</div>
		<picture>
			<source v-if="imageWebp" :srcset="imageWebp" type="image/webp" />
			<source :srcset="image" />
			<img loading="lazy" :src="image" :alt="imageAlt" />
		</picture>
	</nuxt-link>
</template>

<script setup>
const localePath = useLocalePath();

const props = defineProps({
	item: { type: Object, required: true },
	variant: { type: String, default: 'promo' },
});

const link = computed(() => (props.variant === 'room' ? 'rooms' : props.item.link));

const imageWebp = computed(() => (props.variant === 'room' ? props.item.imagePortraitWebp : props.item.imageWebp));

const image = computed(() => (props.variant === 'room' ? props.item.imagePortrait : props.item.image));

const imageAlt = computed(() => (props.variant === 'room' ? props.item.imagePortraitAlt : props.item.imageAlt));

let resizeObserver = null;

function equalizeCardHeaderHeights() {
	const headers = document.querySelectorAll('.fullwidth');
	let maxHeight = 0;

	headers.forEach((header) => {
		header.style.height = 'auto';
	});

	headers.forEach((header) => {
		const height = header.offsetHeight;
		if (height > maxHeight) {
			maxHeight = height;
		}
	});

	headers.forEach((header) => {
		header.style.height = maxHeight + 'px';
	});
}

onMounted(() => {
	equalizeCardHeaderHeights();

	resizeObserver = new ResizeObserver(() => {
		equalizeCardHeaderHeights();
	});

	document.querySelectorAll('.fullwidth').forEach((el) => {
		resizeObserver.observe(el);
	});

	window.addEventListener('resize', equalizeCardHeaderHeights);
});

onBeforeUnmount(() => {
	if (resizeObserver) {
		resizeObserver.disconnect();
	}
	window.removeEventListener('resize', equalizeCardHeaderHeights);
});
</script>

<style scoped>
.card {
	position: relative;
	margin: 0;
	padding: 0;
	overflow: hidden;
}

.card img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	z-index: 1;
	transition: transform 0.4s ease-in-out;
}

.card:hover img {
	transform: scale(1.05);
}

.card::after {
	content: '';
	display: block;
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	z-index: 2;
	background: rgb(0 0 0 / 10%);
	transition: background 0.3s ease-in-out;
}

.card:hover::after {
	background: rgb(0 0 0 / 30%);
}

.card:hover {
	.card-content-header {
		bottom: 70px;
	}
}

.card-content-header {
	position: absolute;
	bottom: 30px;
	left: 30px;
	margin-right: 30px;
	padding: 10px 15px;
	z-index: 4;
	color: var(--header-color);
	text-align: left;
	background: #fff;
	transition: all 0.3s ease-in-out;

	h3 {
		margin: 0;
	}

	h4 {
		font-size: var(--font-size-base) !important;
		margin: 0;
	}

	:deep(p) {
		font-size: 14px;
		color: var(--body-color);
	}
}

.card-content-header.fullwidth {
	h4 {
		font-size: var(--h4-font-size) !important;
		margin-bottom: var(--space-sm);
	}
}

.card-content-button {
	position: absolute;
	bottom: 30px;
	left: 30px;
	padding: 10px;
	z-index: 3;
	color: #fff;
	text-align: left;
	font-size: var(--font-size-xs);
}

.card-content-button svg {
	margin-left: 5px;
	transition: margin-left 0.3s ease-in-out;
	transition-delay: 0s;
}

.card:hover .card-content-button svg {
	margin-left: 16px;
	transition-delay: 0.1s;
}

@media (max-width: 1080px) {
	.column3 {
		width: calc((100% / 12 * 6) - var(--gap) * 0.5);
	}
}

@media (max-width: 620px) {
	.column3 {
		width: 100%;
	}
}
</style>
